import { Badge } from '@/components/ui/badge';
import {
  AnnotationStatus,
  DemoClinicalDetail,
  DemoFieldDiff,
  FieldDiffStatus,
} from '../../api/demo';

const STATUS_LABELS: Record<FieldDiffStatus, string> = {
  match: 'Corrispondenza',
  mismatch: 'Differenza',
  not_scorable: 'Non valutabile',
};

const STATUS_CLASSES: Record<FieldDiffStatus, string> = {
  match: 'bg-success/10 text-success border-success/20',
  mismatch: 'bg-danger/10 text-danger border-danger/20',
  not_scorable: 'bg-muted text-muted-foreground border-border',
};

const ANNOTATION_CLASSES: Record<AnnotationStatus, string> = {
  provisional: 'bg-warning/10 text-warning border-warning/20',
  verified: 'bg-info/10 text-info border-info/20',
  validated: 'bg-success/10 text-success border-success/20',
};

interface DemoAnalysisDiffProps {
  clinical: DemoClinicalDetail;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function annotationFor(
  field: DemoFieldDiff,
  clinical: DemoClinicalDetail,
): AnnotationStatus | null {
  return clinical.field_reviews?.[field.field]?.status ?? field.annotation_status ?? null;
}

/**
 * Confronto expected / proposed / corrected per campo.
 *
 * `not_scorable` non e' un errore dell'analisi: resta visivamente neutro e
 * non contribuisce al conteggio dei mismatch. Il badge di annotazione arriva
 * dal `field_reviews` relativo al campo.
 */
export function DemoAnalysisDiff({ clinical }: DemoAnalysisDiffProps) {
  const fields = clinical.diff?.fields ?? [];

  const counts = fields.reduce(
    (acc, field) => {
      acc[field.status] = (acc[field.status] ?? 0) + 1;
      return acc;
    },
    { match: 0, mismatch: 0, not_scorable: 0 } as Record<FieldDiffStatus, number>,
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-muted-foreground">
          <span data-testid="diff-match-count" className="font-semibold text-success">
            {counts.match}
          </span>{' '}
          corrispondenze
        </span>
        <span className="text-muted-foreground">
          <span data-testid="diff-mismatch-count" className="font-semibold text-danger">
            {counts.mismatch}
          </span>{' '}
          differenze
        </span>
        <span className="text-muted-foreground">
          <span
            data-testid="diff-not-scorable-count"
            className="font-semibold text-foreground"
          >
            {counts.not_scorable}
          </span>{' '}
          non valutabili
        </span>
      </div>

      {fields.length > 0 ? (
        <div className="divide-y divide-border rounded-lg border border-border">
          {fields.map((field) => {
            const annotation = annotationFor(field, clinical);
            return (
              <div
                key={field.field}
                data-testid={`diff-field-${field.field}`}
                data-status={field.status}
                className="grid gap-2 p-3 md:grid-cols-[1.4fr_repeat(3,1fr)] md:items-start"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {field.field.replace(/_/g, ' ')}
                  </span>
                  <Badge
                    variant="outline"
                    data-testid={`diff-status-${field.field}`}
                    className={STATUS_CLASSES[field.status]}
                  >
                    {STATUS_LABELS[field.status]}
                  </Badge>
                  {annotation && (
                    <Badge
                      variant="outline"
                      data-testid={`field-annotation-${field.field}`}
                      className={ANNOTATION_CLASSES[annotation]}
                    >
                      {annotation}
                    </Badge>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Atteso
                  </div>
                  <div className="break-words text-sm text-foreground">
                    {formatValue(field.expected)}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Proposto
                  </div>
                  <div className="break-words text-sm text-foreground">
                    {formatValue(field.proposed)}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Corretto
                  </div>
                  <div className="break-words text-sm text-foreground">
                    {formatValue(field.corrected)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-border p-4 text-center text-sm text-muted-foreground">
          Nessun campo da confrontare
        </div>
      )}
    </div>
  );
}
